<template>
  <v-card flat>
    <v-row align="center" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <autocomplete
          :label="$t('meal_categories')"
          v-model="selectedCategories"
          :items="categoryItems"
          item-text="text"
          item-value="value"
          multiple
          hide-details
        />
      </v-col>
    </v-row>
    <v-card-text class="pa-0">
      <grouped-shift-meals-table
        ref="mealsTable"
        :loading="loading"
        :daily-data="dailyData"
        :meal-categories="mealCategories"
        :selected-shift="selectedShifts"
        :selected-categories="selectedCategories"
        :date-array="dateArray"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import GroupedShiftMealsTable from "@/components/report/shared/GroupedShiftMealsTable.vue";

export default {
  name: "CompanyReportTabGroupedShiftMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel],
  components: {
    GroupedShiftMealsTable,
  },
  props: {
    users: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dailyData: {},
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
      selectedCategories: [], // Filter za kategorije jela
    };
  },
  methods: {
    generateDateArray(startDate, endDate) {
      const dates = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      while (start <= end) {
        dates.push(start.toISOString().substr(0, 10));
        start.setDate(start.getDate() + 1);
      }

      return dates;
    },
    processDailyData() {
      this.dailyData = {};

      if (
        !this.users ||
        !this.users.length ||
        !this.filters.dates.from ||
        !this.filters.dates.to
      )
        return;

      // Generiši datume iz date range-a filters
      const dates = this.generateDateArray(
        this.filters.dates.from,
        this.filters.dates.to
      );

      if (!dates.length) return;

      // Definiši smene na osnovu filtera - sada je niz
      const shiftsToProcess =
        this.selectedShifts && this.selectedShifts.length > 0
          ? this.selectedShifts
          : [1, 2, 3, 4];
      const categoryShiftCombinations = [];

      this.mealCategories.forEach((category) => {
        shiftsToProcess.forEach((shift) => {
          categoryShiftCombinations.push(`${category}_${shift}`);
        });
      });

      // Grupiši podatke po datumima
      dates.forEach((date) => {
        this.dailyData[date] = {};
        categoryShiftCombinations.forEach((combination) => {
          this.dailyData[date][combination] = 0;
        });
      });

      // Saberi količine po datumima, kategorijama i smenama
      dates.forEach((date) => {
        this.mealCategories.forEach((category) => {
          shiftsToProcess.forEach((shift) => {
            const combinationKey = `${category}_${shift}`;

            // Filtriraj korisnike za specifičan datum i smenu, zatim saberi njihove narudžbe za kategoriju
            const totalQuantity = this.users.reduce((sum, user) => {
              // Proveri da li je korisnik za taj datum i smenu
              if (user.date !== date || user.shift !== shift) return sum;

              // Saberi količine za tu kategoriju iz korisnikovih narudžbi
              const userCategoryQuantity = (user.orders || []).reduce(
                (orderSum, order) => {
                  if (order.food?.category?.name === category) {
                    return orderSum + (order.quantity || 0);
                  }
                  return orderSum;
                },
                0
              );

              return sum + userCategoryQuantity;
            }, 0);

            this.dailyData[date][combinationKey] = totalQuantity;
          });
        });
      });
    },
  },
  computed: {
    mealCategories() {
      return this.users.reduce((acc, user) => {
        (user.orders || []).forEach((order) => {
          if (order.food?.category?.name && !acc.includes(order.food.category.name)) {
            acc.push(order.food.category.name);
          }
        });
        return acc;
      }, []);
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.name)
        .join(", ");
    },
    categoryItems() {
      return this.mealCategories.map((category) => ({
        text: this.translate(category),
        value: category,
      }));
    },
    dateArray() {
      if (!this.filters.dates.from || !this.filters.dates.to) return [];

      // Generiši datume iz filters.dates
      return this.generateDateArray(this.filters.dates.from, this.filters.dates.to);
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    users: {
      handler() {
        if (this.users && this.users.length > 0) {
          this.processDailyData();
        }
      },
      immediate: true,
      deep: true,
    },
    selectedShifts: {
      handler() {
        // Kada se promeni shift filter, podaci se automatski osvežavaju preko computed properties
        // Ali možemo da pozvovemo ponovo procesiranje ako je potrebno
        if (this.users && this.users.length > 0) {
          this.processDailyData();
        }
      },
    },
    "filters.dates": {
      handler() {
        if (this.users && this.users.length > 0) {
          this.processDailyData();
        }
      },
      deep: true,
    },
    mealCategories: {
      handler(newCategories) {
        // Ako su kategorije učitane i selectedCategories je prazan, postavi sve kategorije kao default
        if (
          newCategories &&
          newCategories.length > 0 &&
          (!this.selectedCategories || this.selectedCategories.length === 0)
        ) {
          this.selectedCategories = [...newCategories];
        }
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        this.printDocument({
          tables: [
            {
              headers: this.$refs.mealsTable.printHeaders,
              items: this.$refs.mealsTable.printItems,
            },
          ],
          title: [this.$t("report"), "Grupa jela"].join(" "),
          subtitle: this.subtitle,
          beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompanies}</h4>`,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), "Grupa jela"].join(" ") + ".xlsx",
          sheets: [
            {
              name: "Grupa jela",
              headers: this.$refs.mealsTable.headers,
              items: this.$refs.mealsTable.printItems,
              title: [this.$t("Repreportrt"), "Grupa jela"].join(" "),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompanies}</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
